'use client'

import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { InfoIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'

function FieldSet({ className, ...props }: React.ComponentProps<'fieldset'>) {
  return (
    <fieldset
      data-slot="field-set"
      className={cn(
        'flex min-w-0 flex-col gap-4 border-0 p-0',
        'has-[>[data-slot=checkbox-group]]:gap-3',
        className,
      )}
      {...props}
    />
  )
}

function FieldLegend({
  className,
  variant = 'legend',
  ...props
}: React.ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
  return (
    <legend
      data-slot="field-legend"
      data-variant={variant}
      className={cn(
        'mb-2 font-medium',
        'data-[variant=legend]:text-base data-[variant=label]:text-sm',
        className,
      )}
      {...props}
    />
  )
}

function FieldGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-group"
      className={cn(
        'group/field-group @container/field-group flex w-full flex-col gap-4',
        '[&>[data-slot=field-group]]:gap-3',
        className,
      )}
      {...props}
    />
  )
}

const fieldVariants = cva(
  'group/field flex min-w-0 w-full gap-1.5 data-[invalid=true]:text-destructive',
  {
    variants: {
      orientation: {
        vertical: 'flex-col [&>*]:w-full [&>.sr-only]:w-auto',
        horizontal: [
          'flex-row items-center gap-3',
          '[&>[data-slot=field-label-row]]:flex-auto',
          'has-[>[data-slot=field-content]]:items-start',
        ],
        responsive: [
          'flex-col @md/field-group:flex-row @md/field-group:items-center @md/field-group:gap-3',
          '[&>*]:w-full @md/field-group:[&>*]:w-auto [&>.sr-only]:w-auto',
          '@md/field-group:[&>[data-slot=field-label-row]]:flex-auto',
        ],
      },
    },
    defaultVariants: {
      orientation: 'vertical',
    },
  },
)

function Field({
  className,
  orientation = 'vertical',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>) {
  return (
    <div
      role="group"
      data-slot="field"
      data-orientation={orientation}
      className={cn(fieldVariants({ orientation }), className)}
      {...props}
    />
  )
}

function FieldContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-content"
      className={cn(
        'group/field-content flex min-w-0 flex-1 flex-col gap-1.5 leading-snug',
        className,
      )}
      {...props}
    />
  )
}

function FieldLabelRow({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-label-row"
      className={cn('flex min-w-0 items-center gap-1.5', className)}
      {...props}
    />
  )
}

function FieldLabel({
  className,
  children,
  required = false,
  ...props
}: React.ComponentProps<typeof Label> & { required?: boolean }) {
  return (
    <Label
      data-slot="field-label"
      className={cn(
        'group/field-label min-w-0 leading-snug group-data-[disabled=true]/field:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="truncate">{children}</span>
      {required && (
        <>
          <span aria-hidden="true" className="text-destructive">
            *
          </span>
          <span className="sr-only"> (required)</span>
        </>
      )}
    </Label>
  )
}

/**
 * "What is this for?": an info icon that opens a short explanation. A popover
 * rather than a tooltip, so a tap opens it on a phone as well as a click.
 */
function FieldHelp({
  children,
  label = 'More information',
}: {
  children: React.ReactNode
  label?: string
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          data-slot="field-help"
          // No taller than the label, so a field with help lines up with one without.
          className="-my-1.5 size-6 shrink-0 text-muted-foreground"
        >
          <InfoIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-3 text-sm leading-relaxed">{children}</PopoverContent>
    </Popover>
  )
}

function FieldTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="field-title"
      className={cn(
        'text-sm leading-snug font-medium group-data-[disabled=true]/field:opacity-50',
        className,
      )}
      {...props}
    />
  )
}

function FieldDescription({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="field-description"
      className={cn(
        'text-muted-foreground text-xs leading-normal',
        '[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary',
        className,
      )}
      {...props}
    />
  )
}

function FieldSeparator({
  children,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  children?: React.ReactNode
}) {
  return (
    <div
      data-slot="field-separator"
      data-content={Boolean(children)}
      className={cn('relative my-1 h-5 text-xs', className)}
      {...props}
    >
      <Separator className="absolute inset-x-0 top-1/2" />
      {children && (
        <span className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground">
          {children}
        </span>
      )}
    </div>
  )
}

function FieldError({
  className,
  children,
  errors,
  ...props
}: React.ComponentProps<'div'> & {
  errors?: Array<{ message?: string } | undefined>
}) {
  const content = React.useMemo(() => {
    if (children) return children
    if (!errors?.length) return null

    const uniqueErrors = [...new Map(errors.map((error) => [error?.message, error])).values()]

    if (uniqueErrors.length === 1) {
      return uniqueErrors[0]?.message
    }

    return (
      <ul className="ml-4 flex list-disc flex-col gap-1">
        {uniqueErrors.map(
          (error, index) =>
            error?.message && <li key={`${error.message}-${index}`}>{error.message}</li>,
        )}
      </ul>
    )
  }, [children, errors])

  if (!content) return null

  return (
    <div
      role="alert"
      data-slot="field-error"
      className={cn('text-destructive text-xs leading-normal', className)}
      {...props}
    >
      {content}
    </div>
  )
}

export {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldHelp,
  FieldLabel,
  FieldLabelRow,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
}
